/**
 * Analytics, arranged around the one list that is worth money.
 *
 * Conversation counts are vanity. The questions the assistant could not answer
 * are a description, in customers' own words, of what this store either does
 * not sell or does not describe well enough to be found — so that list is
 * above the chart, not below it.
 */

import { useState } from "react";

import { useAnalytics } from "../../api/queries";
import type { DailyPoint, QueryCount } from "../../api/types";
import {
  Badge,
  Card,
  Empty,
  ErrorNote,
  Loading,
  Select,
  Stat,
} from "../../ui/primitives";
import { useSiteContext } from "./SiteLayout";

export function AnalyticsPage() {
  const { orgId, siteId } = useSiteContext();
  const [days, setDays] = useState(30);
  const analytics = useAnalytics(orgId, siteId, days);

  if (analytics.isLoading) return <Loading />;
  if (analytics.error) return <ErrorNote error={analytics.error} />;
  if (!analytics.data) return null;

  const data = analytics.data;
  const missPercent = Math.round(data.miss_rate * 100);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">
          {data.since} to {data.until}
        </h2>
        <Select
          aria-label="Date range"
          value={days}
          onChange={(event) => setDays(Number(event.target.value))}
          className="w-auto"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </Select>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Stat label="Conversations" value={data.conversations} />
        <Stat label="Visitors" value={data.visitors} />
        <Stat
          label="Unanswered"
          value={data.unanswered}
          // A raw count means nothing without its denominator: 40 misses is
          // excellent at 10,000 conversations and alarming at 50.
          hint={`${missPercent}% of searches found nothing`}
          tone={missPercent >= 30 ? "bad" : missPercent >= 15 ? "warn" : "default"}
        />
      </div>

      <Card title="Questions the assistant could not answer">
        {data.unanswered_questions.length === 0 ? (
          <Empty title="Nothing went unanswered">
            Every search in this period found something.
          </Empty>
        ) : (
          <>
            <p className="mb-3 text-sm text-muted">
              These are the visitor&rsquo;s own words. Each one is either a
              product you do not stock, or a page that does not exist yet.
            </p>
            <ul className="divide-y divide-line">
              {data.unanswered_questions.map((row) => (
                <li key={`${row.conversation_id}-${row.asked_at}`} className="py-2">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-sm">{row.question}</span>
                    <span className="text-xs text-muted">
                      {new Date(row.asked_at).toLocaleString()}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>

      <Card title="Most asked">
        {data.top_queries.length === 0 ? (
          <Empty title="No questions yet" />
        ) : (
          <ul className="space-y-1">
            {data.top_queries.map((row) => (
              <li key={row.query}>
                <QueryRow row={row} max={data.top_queries[0]?.count ?? 1} />
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Conversations per day">
        <DailyChart points={data.daily} />
      </Card>
    </div>
  );
}

function QueryRow({ row, max }: { row: QueryCount; max: number }) {
  const width = Math.max(2, Math.round((row.count / max) * 100));

  return (
    <div className="flex items-center gap-3 py-1">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm">{row.query}</span>
          {/* A popular question the assistant keeps missing is the single most
              actionable row on this screen, so it is called out rather than
              left for the reader to compute. */}
          {row.unanswered > 0 && (
            <Badge tone={row.unanswered === row.count ? "bad" : "warn"}>
              {row.unanswered} missed
            </Badge>
          )}
        </div>
        <div
          className="mt-1 h-1.5 rounded-full bg-brand/70"
          style={{ width: `${width}%` }}
          aria-hidden="true"
        />
      </div>
      <span className="text-sm tabular-nums text-muted">{row.count}</span>
    </div>
  );
}

/**
 * A sparkline in inline SVG.
 *
 * No chart library: one series of at most ninety points does not justify
 * 40 KB of dependency, and the accessible fallback below — a real table,
 * visually hidden — is something most chart libraries make harder rather than
 * easier.
 */
function DailyChart({ points }: { points: DailyPoint[] }) {
  if (points.length === 0) return <Empty title="No data yet" />;

  const max = Math.max(1, ...points.map((point) => point.conversations));
  const width = 100;
  const height = 28;
  const step = points.length > 1 ? width / (points.length - 1) : width;

  const line = points
    .map((point, index) => {
      const x = index * step;
      const y = height - (point.conversations / max) * height;
      return `${index === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");

  return (
    <>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="h-24 w-full"
        role="img"
        aria-label={`Conversations per day, peaking at ${max}`}
      >
        <path d={line} fill="none" stroke="currentColor" strokeWidth="0.6" className="text-brand" />
      </svg>

      <div className="mt-1 flex justify-between text-xs text-muted">
        <span>{points[0]?.day}</span>
        <span>peak {max}</span>
        <span>{points.at(-1)?.day}</span>
      </div>

      {/* The same data as a table, for anyone who cannot use the drawing.
          A line chart with no text equivalent is a WCAG 1.1.1 failure. */}
      <details className="mt-2">
        <summary className="cursor-pointer text-xs text-muted">View as table</summary>
        <table className="mt-2 w-full text-xs">
          <thead>
            <tr className="text-left text-muted">
              <th scope="col" className="py-1">Day</th>
              <th scope="col">Conversations</th>
              <th scope="col">Messages</th>
            </tr>
          </thead>
          <tbody>
            {points
              .filter((point) => point.conversations > 0)
              .map((point) => (
                <tr key={point.day}>
                  <td className="py-0.5">{point.day}</td>
                  <td className="tabular-nums">{point.conversations}</td>
                  <td className="tabular-nums">{point.messages}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </details>
    </>
  );
}
